import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import app from 'durandal/app';
import { Box, CircularProgress, DialogContent, DialogTitle, styled } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { noop } from 'lodash-es';
import { useConfirm } from 'material-ui-confirm';
import { Inquiry } from 'persona';
import { InquiryAttributes } from 'persona/dist/lib/interfaces';

import Dialog from '@/components/dialogs/Dialog';
import { ApiService } from '@/features/api';
import { useSyncKoObservable } from '@/hooks/useSyncKoObservable';
import { Instance as companyService } from '@/legacy/services/company';
import { Instance as userService } from '@/legacy/services/user';
import {
  Instance as verificationService,
  PersonaVerificationResponse,
} from '@/legacy/services/verification/verification';

import { useOwnershipVerification } from '../hooks/useOwnershipVerification';
import { useVerification } from '../hooks/useVerification';

type PersonaVerificationBindingModel = {
  companyInformation?: {
    address: {
      address: string;
      city: string;
      country: string;
      state: string;
      suite: string;
      zipCode: string;
    };
    businessUrl: string;
    companyId: string;
    corporationType: string;
    name: string;
    operatingName: string;
  };
  userInformation: {
    userId: string;
    companyId: string;
    /**
     * Only used for KYB
     */
    isCompanyOwner?: boolean;
  };
};

function isSubmitterCompanyOwner(fields: InquiryAttributes): boolean {
  const isSoleProp = fields['entity-type']?.value === 'Sole Prop';
  const isFormFillerOwner = fields['form-filler-is-also-owner']?.value ?? false;
  return isSoleProp || isFormFillerOwner;
}

function mapPersonaCorporationType(corporationType: string): string {
  if (!corporationType) {
    return '';
  }
  const corporationTypeMap: { [key: string]: string } = {
    'Sole Prop': 'Sole Proprietorship',
    Corporation: 'Business Corporation',
    'LLC/Etc': 'Condominium Corporation',
    Charity: 'Nonprofit or Charity (Registered)',
  };
  return corporationTypeMap[corporationType] || '';
}

function mapToPersonaVerificationBindingModel(
  fields: InquiryAttributes,
  companyId: string,
  userId: string,
  isKyc: boolean
): PersonaVerificationBindingModel {
  if (isKyc) {
    return {
      userInformation: {
        userId,
        companyId,
      },
    };
  }
  return {
    companyInformation: {
      companyId,
      name: fields['business-name']?.value,
      operatingName: fields['operating-business-name']?.value,
      corporationType: mapPersonaCorporationType(fields['entity-type']?.value),
      businessUrl: fields['company-website']?.value,
      address: {
        address: fields['business-registered-address-street-1']?.value,
        city: fields['business-registered-address-city']?.value,
        country: fields['business-registered-address-country-code']?.value,
        state: fields['business-registered-address-subdivision']?.value,
        suite: '',
        zipCode: fields['business-registered-address-postal-code']?.value,
      },
    },
    userInformation: {
      userId,
      companyId,
      isCompanyOwner: isSubmitterCompanyOwner(fields),
    },
  };
}

const PersonaFlowContainer = styled(Box)(({ theme }) => ({
  '& > iframe': {
    minHeight: '720px',
    minWidth: '400px',
  },
  [theme.breakpoints.down('sm')]: {
    '& > iframe': {
      minWidth: '300px',
    },
  },
}));

type InquiryDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  trackVerificationCancelled?: (inquiryId: string, referenceId: string) => void;
  trackVerificationInitiated?: (inquiryId: string, referenceId: string) => void;
  trackVerificationSubmitted?: (inquiryId: string, referenceId: string) => void;
};

export const InquiryDialog = ({
  isOpen,
  onClose,
  onSuccess,
  trackVerificationCancelled,
  trackVerificationInitiated,
  trackVerificationSubmitted,
}: InquiryDialogProps) => {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const companyId = useSyncKoObservable(companyService.CompanyId);
  const companyProfile = useSyncKoObservable(companyService.profile);
  const userId = useSyncKoObservable(userService.UserId);
  const userProfile = useSyncKoObservable(userService.profile);
  const [
    {
      companyVerificationCompleted,
      companyVerificationInProgress,
      isKycStep,
      isLoading: isVerificationLoading,
      isUbo,
      userVerification,
    },
    { setCompanyOnboardingStatus, setIsKybSubmittingUser, setUserVerificationSubmitted },
  ] = useVerification();
  const {
    isInitialLoading: isOwnershipVerificationInitialLoading,
    isOwnershipVerificationRequired,
    personaInquiryId: ownershipVerificationPersonaInquiryId,
  } = useOwnershipVerification();

  let referenceType = 'kyb';
  if (isOwnershipVerificationRequired) {
    referenceType = 'cov';
  } else if (companyVerificationCompleted || companyVerificationInProgress) {
    referenceType = 'kyc';
  }
  const referenceId = `${referenceType}:${companyId}:${userId}`;
  const { data: companyPersonaMetadata, isFetching: isCompanyPersonaMetadataLoading } = useQuery({
    enabled: isUbo,
    queryFn: async () =>
      ApiService.get<{ caseId: string }>(
        `${import.meta.env.FEATURE_VERIFICATION_BASE_URL}/companies/${companyId}/companyPersonaMetadata`
      ),
    queryKey: ['companies', companyId, 'companyPersonaMetadata'],
  });
  let existingInquiryId: string | null | undefined;
  try {
    existingInquiryId =
      userVerification?.personaInquiryId ||
      ownershipVerificationPersonaInquiryId ||
      localStorage.getItem(referenceId);
  } catch (_) {
    // Ignore error
  }
  const templateInfo = useMemo(() => {
    // Template ID/Version should not be passed if inquiryId is provided
    // https://docs.withpersona.com/docs/embedded-flow-parameters#resuming-existing-inquiries
    if (existingInquiryId) {
      return null;
    }
    if (isOwnershipVerificationRequired) {
      return {
        templateId: import.meta.env.PERSONA_OWNERSHIP_TEMPLATE_ID,
        templateVersionId: import.meta.env.PERSONA_OWNERSHIP_TEMPLATE_VERSION_ID,
      };
    }
    if (isKycStep) {
      return {
        templateId: import.meta.env.PERSONA_KYC_TEMPLATE_ID,
        templateVersionId: import.meta.env.PERSONA_KYC_TEMPLATE_VERSION_ID,
      };
    }
    return {
      templateId: import.meta.env.PERSONA_GET_STARTED_TEMPLATE_ID,
      templateVersionId: import.meta.env.PERSONA_GET_STARTED_TEMPLATE_VERSION_ID,
    };
  }, [existingInquiryId, isKycStep, isOwnershipVerificationRequired]);
  const prefilledPersonaFields = useMemo(() => {
    const caseId = companyPersonaMetadata?.caseId;
    const res = caseId ? { kyb_case_id: caseId } : {};
    if (isOwnershipVerificationRequired) {
      return {
        ...res,
        form_submitter_id: userId,
      };
    }
    if (isKycStep) {
      return caseId ? res : undefined;
    }
    return {
      ...res,
      email_address: userProfile.email(), // Business/work email address
      form_filler_email_address: userProfile.email(), // User's email address
      business_name: companyProfile.name(),
      business_physical_address_country_code: companyProfile.country(),
      name_first: userProfile.firstName(),
      name_last: userProfile.lastName(),
      phone_number: companyProfile.businessPhone(),
    };
  }, [
    companyPersonaMetadata?.caseId,
    companyProfile,
    isKycStep,
    userProfile,
    isOwnershipVerificationRequired,
    userId,
  ]);

  const onSuccessfulOwnershipSubmission = useCallback(() => {
    onSuccess?.();
    trackVerificationSubmitted?.(existingInquiryId ?? '', referenceId);
    queryClient.invalidateQueries({ queryKey: ['companies', companyId, 'onboarding', 'status'] });
    queryClient.invalidateQueries({
      queryKey: ['companies', companyId, 'verification', 'outstandingVerificationItems'],
    });

    app.trigger('onboarding:company:updated');
    app.trigger('onboarding:identity:updated');

    try {
      localStorage.removeItem(referenceId);
    } catch (_) {
      // Ignore error
    }

    onClose();
  }, [
    companyId,
    existingInquiryId,
    onClose,
    onSuccess,
    queryClient,
    referenceId,
    trackVerificationSubmitted,
  ]);

  const onSuccessfulPersonaSubmission = useCallback(
    (result: PersonaVerificationResponse): void => {
      onSuccess?.();
      if (result.companyVerification) {
        setIsKybSubmittingUser(true);
        setCompanyOnboardingStatus(result.companyVerification.status);
      }
      if (result.userVerification) {
        setUserVerificationSubmitted(true);
      }
      trackVerificationSubmitted?.(existingInquiryId ?? '', referenceId);
      queryClient.invalidateQueries({ queryKey: ['companies', companyId, 'onboarding', 'status'] });
      queryClient.invalidateQueries({
        queryKey: ['companies', companyId, 'verification', 'outstandingVerificationItems'],
      });
      app.trigger('onboarding:company:updated');
      app.trigger('onboarding:identity:updated');
      try {
        localStorage.removeItem(referenceId);
      } catch (_) {
        // Ignore error
      }
      onClose();
    },
    [
      companyId,
      existingInquiryId,
      onClose,
      onSuccess,
      queryClient,
      referenceId,
      setCompanyOnboardingStatus,
      setIsKybSubmittingUser,
      setUserVerificationSubmitted,
      trackVerificationSubmitted,
    ]
  );
  const onUnsuccessfulSubmission = useCallback(
    (error: any): void => {
      const errorMessage = error?.message ?? error?.type;
      confirm({
        description: errorMessage ? (
          <FormattedMessage
            defaultMessage="There was an error submitting your verification: {message}. Please try again later."
            values={{ message: errorMessage }}
          />
        ) : (
          <FormattedMessage defaultMessage="There was an error submitting your verification. Please try again later." />
        ),
        hideCancelButton: true,
        title: <FormattedMessage defaultMessage="Error submitting verification" />,
      }).catch(noop);
    },
    [confirm]
  );

  const { mutate: submitOwnershipVerification, isSuccess: hasCompletedOwnershipVerification } =
    useMutation({
      mutationFn: verificationService.postOwnershipVerification,
      onError: onUnsuccessfulSubmission,
      onSuccess: onSuccessfulOwnershipSubmission,
    });

  const { mutate: submitPersonaVerification, isSuccess: hasCompletedPersonaVerification } =
    useMutation({
      mutationFn: verificationService.postPersonaVerification,
      onError: onUnsuccessfulSubmission,
      onSuccess: onSuccessfulPersonaSubmission,
    });

  const { data: inquirySession, isFetching: isInquirySessionLoading } = useQuery({
    enabled:
      Boolean(existingInquiryId) &&
      (!hasCompletedPersonaVerification || !hasCompletedOwnershipVerification),
    queryFn: async () =>
      ApiService.post<{ sessionToken: string }>(
        `${import.meta.env.FEATURE_VERIFICATION_BASE_URL}/companies/${companyId}/resumePersonaInquiry/${existingInquiryId}`
      ),
    queryKey: ['companies', companyId, 'resumePersonaInquiry', existingInquiryId],
  });

  const handleComplete = useCallback(
    ({ fields }) => {
      if (isOwnershipVerificationRequired) {
        return submitOwnershipVerification(companyId);
      }

      const bindingModel = mapToPersonaVerificationBindingModel(
        fields,
        companyId,
        userId,
        isKycStep
      );
      return submitPersonaVerification(bindingModel);
    },
    [
      companyId,
      isKycStep,
      isOwnershipVerificationRequired,
      submitOwnershipVerification,
      submitPersonaVerification,
      userId,
    ]
  );
  const handleEvent = useCallback(
    (name: string, metadata: Record<string, unknown>): void => {
      // [PLP-37416][PLP-37281] Keep session alive when user interacts with Persona flow. It can
      // take a pretty long time to complete the Persona flow, so we need to keep the session alive.
      // While they are in the iframe, our frame does not receive the window events we use to detect
      // user activity (blur, mousemove, etc.).
      app.trigger('user:keepSessionAlive');
      if (
        name === 'start' &&
        typeof metadata.inquiryId === 'string' &&
        metadata.inquiryId &&
        !existingInquiryId
      ) {
        trackVerificationInitiated?.(existingInquiryId ?? metadata.inquiryId, referenceId);
        try {
          localStorage.setItem(referenceId, metadata.inquiryId);
        } catch (_) {
          // Ignore error
        }
      }
    },
    [existingInquiryId, referenceId, trackVerificationInitiated]
  );
  const handleCancel = useCallback((): void => {
    trackVerificationCancelled?.(existingInquiryId ?? '', referenceId);
  }, [existingInquiryId, referenceId, trackVerificationCancelled]);
  const handleDialogClose = useCallback((): void => {
    if (
      (!hasCompletedPersonaVerification || !hasCompletedOwnershipVerification) &&
      trackVerificationCancelled
    ) {
      trackVerificationCancelled(existingInquiryId ?? '', referenceId);
    }
    onClose();
  }, [
    existingInquiryId,
    hasCompletedOwnershipVerification,
    hasCompletedPersonaVerification,
    onClose,
    referenceId,
    trackVerificationCancelled,
  ]);
  const isLoading =
    isVerificationLoading ||
    isOwnershipVerificationInitialLoading ||
    isCompanyPersonaMetadataLoading ||
    isInquirySessionLoading;
  return (
    <Dialog fullWidth isOpen={isOpen} maxWidth="xs" onDialogClose={handleDialogClose}>
      <DialogTitle>
        <FormattedMessage defaultMessage="Verify your Business and Identity" />
      </DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box alignItems="center" display="flex" justifyContent="center" sx={{ minHeight: 300 }}>
            <CircularProgress />
          </Box>
        ) : (
          <PersonaFlowContainer
            sx={{
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Inquiry
              environmentId={import.meta.env.PERSONA_GET_STARTED_ENVIRONMENT_ID}
              fields={prefilledPersonaFields}
              inquiryId={existingInquiryId}
              onCancel={handleCancel}
              onComplete={handleComplete}
              onEvent={handleEvent}
              referenceId={referenceId}
              sessionToken={inquirySession?.sessionToken}
              templateId={templateInfo?.templateId}
              templateVersionId={templateInfo?.templateVersionId}
            />
          </PersonaFlowContainer>
        )}
      </DialogContent>
    </Dialog>
  );
};
